import {Option, option} from "ts-option";
import PartnerBrandForm from "../../../partnerBrand/PartnerBrandForm";
import {PartnerBrandCurrency} from "../../../../model/partnerBrand/PartnerBrand";
import Brand from "../../../../model/brand/Brand";
import {FormField} from "../../../../validation/FormField";
import BaseFormController from "../../../../validation/BaseFormController";

/**
 * Tabs of the partner brand page, in the order they are shown. The value is what the URL carries in "?tab=".
 */
export enum PartnerBrandTab {
	Company = "company",
	Payouts = "payouts",
	Features = "features",
	Users = "users",
	Legacy = "legacy"
}

export const PARTNER_BRAND_TABS: PartnerBrandTab[] = [
	PartnerBrandTab.Company,
	PartnerBrandTab.Payouts,
	PartnerBrandTab.Features,
	PartnerBrandTab.Users,
	PartnerBrandTab.Legacy
];

export const PARTNER_BRAND_TAB_LABELS: {[tab in PartnerBrandTab]: string} = {
	[PartnerBrandTab.Company]: "Company",
	[PartnerBrandTab.Payouts]: "Payouts",
	[PartnerBrandTab.Features]: "Features",
	[PartnerBrandTab.Users]: "Users",
	[PartnerBrandTab.Legacy]: "Legacy"
};

const COMPANY_FIELDS: Array<keyof PartnerBrandForm> = [
	"brandId", "name", "tradingName", "address", "phoneNumber", "email", "domain", "currency", "vatRegistered",
	"vatRegistrationNumber", "defaultUtilityId", "bdpId", "leadUserId", "logoId", "noteImportant", "freeText"
];

const PAYOUTS_FIELDS: Array<keyof PartnerBrandForm> = [
	"creditValueAirToWaterHp", "creditValueAirToAirHp", "creditValueWaterToWaterHp", "creditValueBrineToWaterHp",
	"creditValueFlowsave", "creditValueCapMode", "creditValueCap"
];

/**
 * Tab showing the given field of the main form. Every field not placed on Company or Payouts is a legacy one.
 */
export function tabOfField(key: string): PartnerBrandTab {
	if (COMPANY_FIELDS.indexOf(key as keyof PartnerBrandForm) >= 0) {
		return PartnerBrandTab.Company;
	}
	if (PAYOUTS_FIELDS.indexOf(key as keyof PartnerBrandForm) >= 0) {
		return PartnerBrandTab.Payouts;
	}
	return PartnerBrandTab.Legacy;
}

/**
 * Names of the form's fields holding a validation error.
 */
export function fieldsWithErrors(form: BaseFormController<any>): string[] {
	return form.fieldKeys().filter(key => ((form as any)[key] as FormField<any>).firstError().isDefined);
}

/**
 * Symbol of the currency selected on the form, used in the labels of amount fields.
 */
export function getCurrencySymbol(form: PartnerBrandForm): string {
	return form.currency.getOptionalValue()
		.map(currency => currency === PartnerBrandCurrency.GBP ? "£" : "€")
		.getOrElse("€");
}

/**
 * Parent brand selected on the form, looked up among the brands already in the store.
 */
export function getSelectedBrand(form: PartnerBrandForm, brands: Option<Brand[]>): Option<Brand> {
	return form.brandId.getOptionalValue()
		.flatMap(brandId => brands.flatMap(all => option(all.find(b => b.id === brandId))));
}
